import { useCallback, useRef } from 'react'

export type CarouselSlideTablistProps = {
    tabCount: number
    activeSlideIndex: number
    itemsToScroll: number
    totalSlides: number
    onRequestChange: (slideIndex: number) => void
    tablistLabel: string
    getPanelIdForSlide: (slideIndex: number) => string
    getTabLabel?: (tabIndex: number) => string
}

export const CarouselSlideTablist = ({
    tabCount,
    activeSlideIndex,
    itemsToScroll,
    totalSlides,
    onRequestChange,
    tablistLabel,
    getPanelIdForSlide,
    getTabLabel = (i) => `Témoignage ${i + 1}`,
}: CarouselSlideTablistProps) => {
    const tablistRef = useRef<HTMLDivElement>(null)

    const focusTab = useCallback((tabIndex: number) => {
        const el = tablistRef.current?.querySelector<HTMLButtonElement>(
            `[data-carousel-tab-index="${tabIndex}"]`
        )
        el?.focus()
    }, [])

    const activateTab = useCallback(
        (tabIndex: number) => {
            const nextSlide = Math.min(
                tabIndex * itemsToScroll,
                Math.max(totalSlides - 1, 0)
            )
            onRequestChange(nextSlide)
        },
        [itemsToScroll, onRequestChange, totalSlides]
    )

    const onTabKeyDown = useCallback(
        (e: React.KeyboardEvent, tabIndex: number) => {
            if (tabCount <= 0) return
            let next = tabIndex
            switch (e.key) {
                case 'ArrowRight':
                case 'ArrowDown':
                    e.preventDefault()
                    next = (tabIndex + 1) % tabCount
                    break
                case 'ArrowLeft':
                case 'ArrowUp':
                    e.preventDefault()
                    next = (tabIndex - 1 + tabCount) % tabCount
                    break
                case 'Home':
                    e.preventDefault()
                    next = 0
                    break
                case 'End':
                    e.preventDefault()
                    next = tabCount - 1
                    break
                default:
                    return
            }
            activateTab(next)
            requestAnimationFrame(() => focusTab(next))
        },
        [activateTab, focusTab, tabCount]
    )

    if (tabCount < 1) return null

    return (
        <div
            ref={tablistRef}
            role="tablist"
            aria-label={tablistLabel}
            className="flex w-full mt-4 justify-center gap-2 flex-wrap rounded-xl p-2 -m-2 outline-none focus-within:outline focus-within:outline-2 focus-within:outline-[#0a8335] focus-within:outline-offset-2"
        >
            {Array.from({ length: tabCount }, (_, tabIndex) => {
                const firstSlideOfPage = tabIndex * itemsToScroll
                const isSelected =
                    activeSlideIndex >= firstSlideOfPage &&
                    activeSlideIndex <
                        Math.min(firstSlideOfPage + itemsToScroll, totalSlides)
                const panelId = getPanelIdForSlide(firstSlideOfPage)
                return (
                    <button
                        key={tabIndex}
                        type="button"
                        role="tab"
                        data-carousel-tab-index={tabIndex}
                        tabIndex={isSelected ? 0 : -1}
                        aria-selected={isSelected}
                        aria-controls={panelId}
                        aria-label={getTabLabel(tabIndex)}
                        className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 focus-visible:rounded-full"
                        style={{
                            height: 16,
                            width: 16,
                            borderRadius: '50%',
                            border: 0,
                            background: isSelected ? '#0A8335' : '#dedede',
                            margin: 5,
                            padding: 0,
                        }}
                        onClick={() => activateTab(tabIndex)}
                        onKeyDown={(e) => onTabKeyDown(e, tabIndex)}
                        onFocus={() => {
                            if (!isSelected) {
                                activateTab(tabIndex)
                            }
                        }}
                    />
                )
            })}
        </div>
    )
}
